import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import type { EpisodeOfCareLifeCycleUpdate } from "@/types/EpisodeOfCareLifecycle";
import { useQueryClient } from "@tanstack/react-query";
import type * as fhirTypes from "fhir/r4";

interface DeleteAssessmentResponse {
  data: {
    documentReference: fhirTypes.DocumentReference;
    episodeOfCareLifecycleUpdate?: EpisodeOfCareLifeCycleUpdate;
  };
}

export const useDeleteAssessment = ({
  patientId,
  motherEncounterId,
  encounterId,
  docRefId,
  onError,
}: {
  patientId?: string;
  motherEncounterId?: string;
  encounterId?: string;
  docRefId?: string;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<DeleteAssessmentResponse, void>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.EpisodesOfCare.Assessment.DELETE(
        patientId,
        docRefId,
      ),
      mutationFn: () => {
        return axiosInstance.delete(
          `/charting/encounter-charting/${encounterId}/assessment`,
        );
      },
      onSuccess: (response) => {
        if (response.data?.episodeOfCareLifecycleUpdate) {
          queryClient.invalidateQueries({
            queryKey: QueryKeys.Patient.Chart.EpisodesOfCare.DEFAULT({
              patientId,
              encounterId: motherEncounterId,
            }),
          });
        }

        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.EpisodesOfCare.Assessment.DEFAULT({
            patientId,
            encounterId,
          }),
        });

        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Timeline.ENCOUNTER({
            patientId,
            motherEncounterId,
            encounterId,
          }),
        });

        if (response.data?.episodeOfCareLifecycleUpdate) {
          queryClient.invalidateQueries({
            queryKey:
              QueryKeys.Patient.MedicalRecords.MedicalHistory.DEFAULT(
                patientId,
              ),
          });
        }
      },
      onError: (error) => {
        onError?.(error);
      },
    },
  });
};
